// Scene 4 - configuration (stage redesign).
//
// Model:
//   - The BASE VALUE (first forward stage) is set ONCE and inherited by all later stages. It is
//     the only mandatory entry (progress count).
//   - For every further stage the user can DELIBERATELY add a deviation via "+". Without one the
//     stage inherits silently and gets no entry of its own in Git (see buildConfigsByWerk).
//   - Displayed values come FROM GIT wherever something is maintained there; the tenant's current
//     value is only the fallback. The other way round, a transport to QAS would overwrite the
//     maintained PROD values.
//
// UI:
//   - Base value: compact field, a click opens an overlay editor inside the cell.
//   - Further stage: "inherits: <value>" with a "+" hint; on a deviation an information field with a
//     reset. While editing, a spotlight dims the other cells and the columns do NOT shift (the
//     overlay is absolutely positioned).
//   - Single-stage space (source + target): exactly one base-value column, so visually
//     "parameter | source | target | status", no regression.
//
// The data model (entry.newValue = base, entry.byStage[stage] = deviation) and the reducer
// PATCH_CFG_ENTRY are unchanged. The snapshot contract is buildConfigsByWerk: base value and
// deviations travel as values ({ __base__, <stage> }) per plant and source artifact to the
// backend, which writes SAP's parameter file per target package.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip, Select, MenuItem, FormHelperText } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { inheritanceChain } from "../../../utils/stageTargets";
import { sameParameterValue } from "../../../utils/xmlEntities";
import Skeleton from "../../../components/Skeleton";
import { useLang } from "../i18n";
import { useTransportV2, usesWerke, buckets, vorwaertsZiele, vorwaertsAlleZiele, vorwaertsZielKette } from "../context/TransportV2Context";
import { useArtifactConfigs } from "../hooks/useArtifactConfigs";
import { useGitConfigs } from "../hooks/useGitConfigs";
import TargetName from "../components/TargetName";
import { isPackageTransport, packageDesignObjects } from "../utils/packageTransport";
import { classifyConfigStatus, isEmptyBooleanAdapterDefault, adapterFieldOptions, adapterFieldName, adapterFieldValueLabel, normalizeAdapterFieldValue } from "../../../utils/transport-config-helpers";

function bucketByGroup(entries) {
  const sender = [], receiver = [], other = [];
  for (const e of entries || []) {
    if (!e) continue;
    if (e.group === "sender") sender.push(e);
    else if (e.group === "receiver") receiver.push(e);
    else other.push(e);
  }
  return { sender, receiver, other };
}

// Pflichtfeld-/Offen-Zaehlung auf der BASIS-Stufe (= Root des Eintrags). Spaetere
// Stufen sind optionale Overlays und werden NIE als "offen" gemeldet - sonst wuerde
// der Fortschrittsbalken bei jedem Overlay-Feld auf 0 fallen.
// req, leer und opt getrennt: "Pflichtfeld offen" meldet NUR echte Pflichtfelder (SAP propdef
// isRequired=true, Backend liefert `required`). leer = kein Wert und auch kein Quellwert, der
// greifen würde; das ist nur eine Warnung. opt = leer, aber der Quellwert greift beim Transport.
function countMissing(entries) {
  let req = 0, opt = 0, leer = 0, total = 0;
  for (const e of entries || []) {
    if (!e) continue;
    total++;
    const st = classifyConfigStatus(e);
    if (st === "required") req++;
    // Leere Checkbox (xsd:boolean) = Adapter-Standard in SAP: gilt als gesetzt, zählt nicht als leer.
    else if (st === "empty" && !isEmptyBooleanAdapterDefault(e)) { if (effektiverBasisWert(e)) opt++; else leer++; }
  }
  return { miss: req + leer + opt, req, leer, opt, total };
}

// Kurztext und Farbe für einen Zähler: Pflicht rot, leere Felder als Warnung, sonst neutral.
function missingHint(t, c) {
  if (c.req > 0) return { cls: "crit", text: c.req === 1 ? t("s4.missingOne") : t("s4.missing", { n: c.req }) };
  if (c.leer > 0) return { cls: "warn", text: c.leer === 1 ? t("s4.emptyOne") : t("s4.emptyN", { n: c.leer }) };
  if (c.opt > 0) return { cls: "idle", text: c.opt === 1 ? t("s4.optOpenOne") : t("s4.optOpen", { n: c.opt }) };
  return { cls: "info", text: t("s4.allset") };
}

// Der effektive Basiswert eines Eintrags: der gesetzte newValue, sonst - fuer
// Nicht-Sender/Receiver - der Quell-Default (der beim Transport ohnehin greifen wuerde).
// Fuer Sender/Receiver ohne Wert bleibt er leer (Warnung; Pflicht nur bei SAP isRequired=true).
function effektiverBasisWert(entry) {
  if (!entry) return "";
  if (entry.newValue) return entry.newValue;
  // A2: Benutzer hat den Wert ausdrücklich geleert.
  if (entry.modified) return "";
  const isSR = entry.group === "sender" || entry.group === "receiver";
  return isSR ? "" : (entry.defaultValue || "");
}

// Wert, den eine spaetere Stufe erbt: naechste Vorstufe mit eigener Abweichung,
// sonst der effektive Basiswert. Gibt zusaetzlich den Stufen-Code der Herkunft.
// Erbkette im Mehrzielmodus (Spalten mit targetId, Vertrag V.11): die Standardziele früherer Stufen, absteigend, ohne
// Geschwister derselben Stufe, danach der Basiswert. Die Kette läuft über ALLE Ziele und hängt nicht an der Auswahl;
// ein abgewähltes Standardziel bleibt Kettenglied (`ctx.alle`, `ctx.kette`). Ohne targetId gilt die bisherige Stufenkette.
export function geerbterWert(entry, stufen, sidx, ctx) {
  const spalte = stufen[sidx];
  if (ctx && spalte?.targetId !== undefined) {
    const basisKey = ctx.alle[0]?.key;
    for (const key of inheritanceChain(ctx.kette, spalte.stageKey, spalte.targetId)) {
      if (key === basisKey) continue;
      const v = entry?.byStage?.[key]?.newValue;
      if (v) { const from = ctx.alle.find(item => item.key === key); return { value: v, code: from?.code, from, reason: "stage" }; }
    }
    const basis = ctx.alle[0] || stufen[0];
    return { value: effektiverBasisWert(entry), code: basis?.code || "", from: basis, reason: "base" };
  }
  for (let i = sidx - 1; i >= 1; i--) {
    const s = stufen[i];
    const v = entry?.byStage?.[s.key]?.newValue;
    if (v) return { value: v, code: s.code, from: s, reason: "stage" };
  }
  const basis = stufen[0];
  return { value: effektiverBasisWert(entry), code: basis?.code || "", from: basis, reason: "base" };
}

// Hat der Eintrag auf irgendeiner Zusatzstufe eine echte Abweichung (Wert gesetzt
// UND != geerbter Wert)?
function hatAbweichung(entry, stufen, ctx) {
  for (let i = 1; i < stufen.length; i++) {
    const ov = entry?.byStage?.[stufen[i].key]?.newValue;
    if (!ov) continue;
    // Nur anders maskiert (`&lt;` gegen `<`) ist keine Abweichung.
    if (!sameParameterValue(ov, geerbterWert(entry, stufen, i, ctx).value)) return true;
  }
  return false;
}

const EDIT_GLYPH = (
  <svg className="edit-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
    <path d="m18.5 2.5 3 3-9 9H9v-3z" />
  </svg>
);

export default function Scene4Config() {
  const { t, lang } = useLang();
  const { state, dispatch } = useTransportV2();
  const { loadFor } = useArtifactConfigs();
  const gitCfgs = useGitConfigs();
  const parametersNotRequired = gitCfgs.requirement === "not-required";
  const [openAcc, setOpenAcc] = useState({});
  const [showWarn, setShowWarn] = useState(false);

  // Overlay-Editor. editing = { artId, paramKey, stageKey, isBasis, stageCode,
  // sourceDefault, inheritedValue, inheritedCode, hasOverride }. editVal = Eingabe.
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const editValRef = useRef("");
  const editingRef = useRef(null);
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  // Auswahlfeld (MUI Select): das Menü liegt in einem Portal AUSSERHALB von editorRef. Solange es
  // offen ist, dürfen Außenklick und Blur den Editor nicht schließen (sonst geht die Auswahl verloren).
  const selectOpenRef = useRef(false);
  const inSelectMenu = (el) => !!(el && typeof el.closest === "function" && el.closest(".s4-select-menu"));
  // Anzeige eines Werts eines bekannten Auswahlfelds: Bezeichnung mit Schlüssel, z. B. "Internet (default)".
  const shown = (entry, value) => adapterFieldValueLabel(entry?.adapterField, value, lang);
  useEffect(() => { editValRef.current = editVal; }, [editVal]);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  const bucketList = buckets(state);
  const withWerke = usesWerke(state);
  const tab = state.cfgTab || bucketList[0]?.key || null;

  // Spalten: je Stufe, im Mehrzielmodus je gewähltem Ziel (Schlüssel = targetKey), sonst wie bisher je Stufe.
  const multi = state.multiTarget === true;
  const stufen = useMemo(() => vorwaertsZiele(state), [state.space, state.multiTarget, state.stageTargets, state.stageTargetSel]); // eslint-disable-line react-hooks/exhaustive-deps
  const basisStage = stufen[0] || null;
  const chainCtx = useMemo(() => (multi ? { alle: vorwaertsAlleZiele(state), kette: vorwaertsZielKette(state) } : undefined), [multi, state.space, state.stageTargets]); // eslint-disable-line react-hooks/exhaustive-deps
  // Stabile Identitaet, damit openEditor (useCallback) nicht bei jedem Render neu bindet.
  const effektiveStufen = useMemo(() => (stufen.length ? stufen : (basisStage ? [basisStage] : [])), [stufen, basisStage]);
  const stageCount = effektiveStufen.length || 1;

  // Quell-Stufen-Code fuer die Spaltenueberschrift "Quelle (<code>)".
  const quellCode = useMemo(() => {
    const s = state.space;
    if (!s || !Array.isArray(s.stages) || !s.quellStufe) return "";
    return s.stages.find((st) => st.stageKey === s.quellStufe)?.code || "";
  }, [state.space]);

  useEffect(() => {
    if (!state.cfgTab && bucketList[0]) dispatch({ type: "SET_CFG_TAB", payload: bucketList[0].key });
  }, [state.cfgTab, bucketList, dispatch]);

  const artefakteInBucket = useCallback((werkKey) => {
    const list = [];
    for (const [origId, sel] of Object.entries(state.sel || {})) {
      if (!sel || !sel[werkKey]) continue;
      const precheckKey = Object.keys(state.precheckByBucket || {}).find((k) => {
        const e = state.precheckByBucket[k];
        return e?.werk?.kennung === (werkKey === "std" ? undefined : werkKey);
      }) || (werkKey === "std" ? Object.keys(state.precheckByBucket || {})[0] : null);
      // G: Kein Rückfall auf einen fremden Vergleich — fehlt der Vergleich für ein Werk,
      // wird das Werk nicht geladen (keine fremde Ziel-ID verwenden).
      if (!precheckKey) continue;
      const entry = state.precheckByBucket?.[precheckKey];
      const item = entry?.comparison?.find((a) => (a.originalId || a.Id) === origId);
      if (!item) continue;
      // Zielname und Ziel-ID nur aus dem Vergleich genau dieses Werks anzeigen, nie aus einem fremden Bucket.
      const ownPlant = werkKey !== "std" && entry?.werk?.kennung === werkKey;
      list.push({
        id: origId,
        name: item.Name || origId,
        suffixedId: item.suffixedId || item.Id,
        ownPlant,
        targetName: ownPlant && typeof item.targetName === "string" ? item.targetName : null,
        status: String(item.status || "").toLowerCase(),
      });
    }
    return list;
  }, [state.sel, state.precheckByBucket]);

  // Keep the read generation independent of loading-state changes. Git and SAP
  // start together; the shared promise prevents tenant values becoming editable
  // before the Git values have been validated. Selection/navigation cancels the
  // previous generation, including any queued artifact reads.
  const configReadScope = JSON.stringify([
    state.spaceId, state.pkgId, state.packageEpoch, state.sourceInstanceId,
    state.configReadGeneration, gitCfgs.scopeKey, state.plantNaming, state.space?.qasInstance?.id,
    Object.entries(state.precheckByBucket || {}).map(([key, entry]) => [key, entry.plantTargetRevision]),
    bucketList.map(b => [b.key, b.kennung, artefakteInBucket(b.key).map(a => [a.id, a.suffixedId])]),
    vorwaertsAlleZiele(state).map(st => [st.key, st.instanceId]),
  ]);
  useEffect(() => {
    if (!state.sourceInstanceId) return;
    const controller = new AbortController();
    const readScope = JSON.stringify([gitCfgs.scopeKey, state.configReadGeneration]);
    const ready = (["da", "leer"].includes(state.gitCfgState) || (parametersNotRequired && state.gitCfgState === "not-required")) && !state.gitCfgUnvollstaendig
      && state.gitCfgScope === gitCfgs.scopeKey
      ? Promise.resolve(true)
      : Promise.resolve(gitCfgs.laden({ signal: controller.signal }));
    const targetInst = state.space?.qasInstance;
    const tasks = targetInst && !parametersNotRequired ? bucketList.flatMap(b => artefakteInBucket(b.key).map(a => ({
      sourceInstanceId: state.sourceInstanceId,
      targetInstanceId: targetInst.id,
      sourceArtifactId: a.id,
      suffixedId: a.suffixedId,
      werkKey: b.key,
      werkKennung: b.key === "std" ? "" : b.kennung || "",
    }))) : [];
    // Bound the fan-out for large selections and plant splits. Each read keeps
    // all forward stages; the limit changes scheduling, not the transport scope.
    let next = 0;
    const worker = async () => {
      while (!controller.signal.aborted && next < tasks.length) {
        const task = tasks[next++];
        try { await loadFor(task, { signal: controller.signal, commitAfter: ready, readScope }); }
        catch { /* The hook records the scoped read error. */ }
      }
    };
    for (let i = 0; i < Math.min(3, tasks.length); i++) worker();
    return () => controller.abort();
    // Loading/cfg updates belong to this generation and must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configReadScope]);

  const counts = useCallback((werkKey) => {
    let miss = 0, total = 0, req = 0, opt = 0, leer = 0;
    for (const a of artefakteInBucket(werkKey)) {
      const entries = state.cfg?.[werkKey]?.[a.id]?.entries;
      const c = countMissing(entries);
      miss += c.miss;
      req += c.req;
      opt += c.opt;
      leer += c.leer;
      total += c.total;
    }
    return { filled: total - miss, total, req, opt, leer };
  }, [artefakteInBucket, state.cfg]);

  const activeCounts = useMemo(() => {
    let miss = 0, total = 0, req = 0, opt = 0, leer = 0;
    for (const b of bucketList) {
      const c = counts(b.key);
      miss += (c.total - c.filled);
      req += c.req;
      opt += c.opt;
      leer += c.leer;
      total += c.total;
    }
    return { filled: total - miss, total, req, opt, leer };
  }, [bucketList, counts]);

  const progressPct = activeCounts.total ? Math.round(activeCounts.filled / activeCounts.total * 100) : 100;
  // Getrennt fuer die Anzeige: nur echte Pflichtfelder duerfen als solche gemeldet
  // werden; optional-leere Felder bekommen einen neutralen Hinweis ohne Alarmfarbe.
  const offenReq = activeCounts.req;
  const onWeiter = () => {
    if (cfgLaedtIrgendwo) return;
    // Warnen nur bei ECHTEN Pflichtfeldern: nur-optionale Leerfelder sind erlaubt
    // (der Fusstext meldet sie neutral) - ein Warn-Popup dazu widersprach dem und
    // erschien selbst nach "Alle Vorschlagswerte uebernehmen" noch.
    if (offenReq > 0) { setShowWarn(true); return; }
    dispatch({ type: "SET_CONFIG_REVIEWED" });
    dispatch({ type: "SET_STEP", payload: 5 });
  };
  const bestaetigeWeiter = () => {
    setShowWarn(false);
    dispatch({ type: "SET_CONFIG_REVIEWED" });
    dispatch({ type: "SET_STEP", payload: 5 });
  };

  const activeBucket = bucketList.find((b) => b.key === tab);
  const arts = tab ? artefakteInBucket(tab) : [];
  // Pakettransport (W6.5): Designobjekte reisen ohne Parameter mit, die iFlows bleiben wie bisher.
  const wholePackage = isPackageTransport(state);
  const designCount = wholePackage ? packageDesignObjects(state).length : 0;
  const packageWithoutIflows = parametersNotRequired;
  const targetInst = state.space?.qasInstance;
  const cfgFailed = !parametersNotRequired && (state.gitCfgState === "fehler" || state.gitCfgUnvollstaendig || Object.values(state.cfgErrors || {}).some(Boolean));
  // Ladezustand: erst die Git-Werte, dann je Artefakt die Werte der Ziel-Instanz. Solange eines
  // davon fehlt, zeigt die Ansicht Platzhalter statt Felder mit Vorgabewerten. Ohne Ziel-Instanz
  // oder Quelle laedt der Effekt oben nichts - dann wie bisher sofort das Formular.
  const cfgLaedt = !parametersNotRequired && !!targetInst && !!state.sourceInstanceId && (
    ["idle", "laedt", "not-required"].includes(state.gitCfgState) || arts.some((a) => !state.cfg?.[tab]?.[a.id])
  );
  // "Alle Vorschlagswerte uebernehmen" und "Weiter" wirken ueber ALLE Werke - sie warten deshalb,
  // bis jeder Reiter geladen ist, nicht nur der sichtbare.
  const cfgLaedtIrgendwo = !parametersNotRequired && !!targetInst && !!state.sourceInstanceId && (
    ["idle", "laedt", "fehler", "not-required"].includes(state.gitCfgState) || state.gitCfgUnvollstaendig
    || Object.values(state.cfgErrors || {}).some(Boolean)
    || bucketList.some((b) => artefakteInBucket(b.key).some((a) => !state.cfg?.[b.key]?.[a.id]))
  );

  // --- Patches ------------------------------------------------------------
  const onPatchBasis = (werkKey, sourceId, paramKey, value) => {
    dispatch({
      type: "PATCH_CFG_ENTRY",
      payload: { werkKey, sourceId, paramKey, patch: { newValue: value, modified: true, fromTarget: false, fromGit: false } },
    });
  };
  const onPatchStage = (werkKey, sourceId, paramKey, stageKey, value) => {
    dispatch({
      type: "PATCH_CFG_ENTRY",
      payload: { werkKey, sourceId, paramKey, stage: stageKey, patch: { newValue: value, modified: true, fromTarget: false, fromGit: false } },
    });
  };

  // Apply or undo the package's bulk change atomically. Only base values are filled.
  const onFillAll = () => {
    dispatch({ type: "TOGGLE_CFG_SUGGESTIONS" });
  };

  // --- Overlay-Editor -----------------------------------------------------
  const openEditor = useCallback((artId, entry, st, sidx) => {
    const isBasis = sidx === 0;
    let inheritedValue = "", inheritedCode = "", hasOverride = false, cur = "", origValue = "";
    if (isBasis) {
      // NUR den aktuellen Wert vorbelegen (leer = leer). Der Quell-Default steht als
      // Placeholder im Feld - so committet ein reines Aufklappen + Ausserklick nichts.
      cur = entry.newValue || "";
      origValue = entry.newValue || "";
    } else {
      const inh = geerbterWert(entry, effektiveStufen, sidx, chainCtx);
      inheritedValue = inh.value;
      inheritedCode = inh.code;
      const ov = entry?.byStage?.[st.key]?.newValue || "";
      hasOverride = !!ov && !sameParameterValue(ov, inheritedValue);
      // Ein nur anders maskierter Stufenwert gilt als geerbt: das Feld zeigt den geerbten Wert, und Übernehmen
      // entfernt die Scheinabweichung (desired "" unten).
      cur = hasOverride ? ov : inheritedValue;
      origValue = ov; // tatsaechlich gespeicherte Abweichung ("" = keine)
    }
    setEditing({
      artId, paramKey: entry.ParameterKey, stageKey: st.key, isBasis,
      stageCode: st.code, sourceDefault: entry.defaultValue || "",
      inheritedValue, inheritedCode, hasOverride, origValue,
    });
    setEditVal(cur);
  }, [effektiveStufen, chainCtx]);

  const closeEditor = useCallback(() => {
    setEditing(null);
    setEditVal("");
  }, []);

  const commitEditor = useCallback(() => {
    const ed = editingRef.current;
    if (!ed) { closeEditor(); return; }
    const v = editValRef.current;
    if (ed.isBasis) {
      // Nur schreiben, wenn sich der Wert wirklich geaendert hat. Sonst wuerde ein
      // reines Ansehen (oeffnen + Ausserklick) fromTarget/Status umkippen.
      if (v !== ed.origValue) onPatchBasis(tab, ed.artId, ed.paramKey, v);
    } else {
      // Leer oder identisch zum geerbten Wert = keine Abweichung. Nur schreiben, wenn
      // sich der Overlay-Zustand tatsaechlich aendert.
      const desired = (!v || sameParameterValue(v, ed.inheritedValue)) ? "" : v;
      if (desired !== ed.origValue) onPatchStage(tab, ed.artId, ed.paramKey, ed.stageKey, desired);
    }
    closeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, closeEditor]);

  const resetOverride = useCallback((artId, paramKey, stageKey) => {
    onPatchStage(tab, artId, paramKey, stageKey, "");
    closeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, closeEditor]);

  // Aussenklick uebernimmt (kein Datenverlust); Esc verwirft, Enter uebernimmt.
  useEffect(() => {
    if (!editing) return;
    const onDown = (ev) => {
      // Offenes Auswahlmenü (MUI-Portal) gehört zum Editor: kein vorzeitiges Übernehmen.
      if (selectOpenRef.current || inSelectMenu(ev.target)) return;
      if (editorRef.current && !editorRef.current.contains(ev.target)) commitEditor();
    };
    const id = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => { clearTimeout(id); document.removeEventListener("mousedown", onDown); };
  }, [editing, commitEditor]);

  // Editor beim Tab-/Paketwechsel schliessen.
  useEffect(() => { closeEditor(); }, [tab, closeEditor]);

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const n = el.value.length;
      try { el.setSelectionRange(n, n); } catch { /* type=text ok */ }
    } else if (editing && editorRef.current) {
      // Auswahlfeld: das sichtbare Select-Element fokussieren (MUI hält den Wert in einem versteckten Input).
      const el = editorRef.current.querySelector('[role="combobox"], .MuiSelect-select');
      if (el) el.focus();
    }
    selectOpenRef.current = false;
  }, [editing]);

  // Mehrzielmodus: Herkunft des Werts nur im Tooltip, Warnung "Zielwert fehlt" bei Sender und Receiver ohne Zielwert.
  const spaltenName = st => [st?.code, st?.region || (st?.isDefault ? "" : st?.name)].filter(Boolean).join(" ");
  const cellValues = (entry, st, sidx) => sidx === 0 ? entry : entry?.byStage?.[st.key];
  const targetMissing = (entry, st, sidx) => multi && (entry?.group === "sender" || entry?.group === "receiver")
    && !(cellValues(entry, st, sidx)?.targetValue);
  const tip = (title, node) => (multi ? <Tooltip title={title} describeChild placement="top" enterDelay={300}
    componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}>{node}</Tooltip> : node);
  const missingCount = (entry) => (multi ? effektiveStufen.filter((st, sidx) => targetMissing(entry, st, sidx)).length : 0);
  const originTitle = (entry, st, sidx, inh, value) => {
    const own = cellValues(entry, st, sidx);
    let origin;
    if (own?.fromGit) origin = t("s4.originGit");
    else if (own?.fromTarget) origin = t("s4.originTarget", { name: st.name || st.code });
    else if (sidx > 0 && !own?.newValue) origin = t("s4.originInherit", { from: spaltenName(inh.from), reason: t(inh.reason === "stage" ? "s4.reasonStage" : "s4.reasonBase") });
    else origin = t("s4.originManual");
    return [value, origin].filter(Boolean).join("\n");
  };

  const computeRowStatus = (entry, isEditingRow) => {
    if (isEditingRow) return { cls: "idle", label: t("s4.rowEditing") };
    // F: Quelle nicht konfiguriert, Git-Wert veraltet — Hinweis je Zeile.
    if (entry?.gitStale) return { cls: "warn", label: t("s4.rowGitStale") };
    const base = classifyConfigStatus(entry);
    if (base === "required") return { cls: "crit", label: t("s4.rowBaseMissing") };
    // Bekanntes Auswahlfeld mit einem Wert, den SAP nicht kennt: gemeldet, aber nicht gesperrt.
    if (base === "invalid") return { cls: "crit", label: t("s4.rowInvalid") };
    // Leer ohne greifenden Quellwert: nur Warnung, nie Pflicht (Pflicht nur bei SAP isRequired=true).
    if (base === "empty" && isEmptyBooleanAdapterDefault(entry)) return { cls: "idle", label: t("s4.rowAdapterDefault") };
    if (base === "empty") return effektiverBasisWert(entry) ? { cls: "idle", label: t("s4.rowOpen") } : { cls: "warn", label: t("s4.rowEmpty") };
    if (hatAbweichung(entry, effektiveStufen, chainCtx)) return { cls: "info", label: t("s4.rowDiverges") };
    // Preloaded from Git. Checked before "from target" because it beats it - without that
    // distinction a maintained value would look like a hand-typed one.
    if (entry?.fromGit) return { cls: "info", label: t("s4.rowFromGit") };
    if (base === "fromTarget") return { cls: "info", label: t("s4.rowFromTarget") };
    return { cls: "info", label: t("s4.rowSet") };
  };

  const renderEditor = (entry, st, sidx) => {
    const isBasis = sidx === 0;
    const ed = editing;
    const aria = isBasis
      ? t("s4.edAriaBasis", { param: entry.ParameterKey })
      : t("s4.edAriaOverride", { param: entry.ParameterKey, stage: st.code });
    const hint = isBasis
      ? t("s4.edSourceHint", { v: shown(entry, entry.defaultValue) || "-" })
      : t("s4.edInheritHint", { stage: ed.inheritedCode || basisStage?.code || "", v: shown(entry, ed.inheritedValue) || "-" });
    const placeholder = isBasis ? (entry.defaultValue || "") : (ed.inheritedValue || "");
    // Editor der letzten (rechtesten) Stufe oeffnet nach links, damit er nicht ueber
    // den rechten Rand laeuft; die uebrigen oeffnen nach rechts (naeher am Feld).
    const toLeft = sidx > 0 && sidx === effektiveStufen.length - 1;
    // Bekanntes Auswahlfeld (z. B. Proxy Type): Auswahl statt Freitext. Die Auswahl übernimmt direkt;
    // ein unbekannter Altwert bleibt als nicht wählbarer Eintrag sichtbar und wird nicht still ersetzt.
    const options = adapterFieldOptions(entry.adapterField);
    const current = options ? normalizeAdapterFieldValue(entry.adapterField, editVal) : null;
    const cell = isBasis ? entry : entry?.byStage?.[st.key];
    const mappedFrom = cell && !cell.modified ? cell.mappedFrom : null;
    const chooseOption = (value) => {
      editValRef.current = value;
      setEditVal(value);
      commitEditor();
    };
    return (
      <div
        ref={editorRef}
        className={"cell-editor" + (toLeft ? " to-left" : "")}
        role="group"
        aria-label={aria}
        onBlur={(e) => {
          if (selectOpenRef.current || inSelectMenu(e.relatedTarget)) return;
          if (editorRef.current && !editorRef.current.contains(e.relatedTarget)) commitEditor();
        }}
      >
        <div className="ce-lbl">
          <span className="code">{st.code}</span>
          <span>{isBasis ? t("s4.hdrBasis") : t("s4.addOverride")}</span>
          <span className="inh" title={hint}>{hint}</span>
        </div>
        {options ? (
          <>
            <Select
              className="ce-select"
              value={editVal}
              displayEmpty
              error={!current.valid}
              IconComponent={ExpandMoreRounded}
              inputProps={{ "aria-label": aria }}
              onOpen={() => { selectOpenRef.current = true; }}
              onClose={() => { selectOpenRef.current = false; }}
              onChange={(e) => chooseOption(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape" && !selectOpenRef.current) { e.preventDefault(); closeEditor(); } }}
              renderValue={(v) => {
                if (!v) return <span style={{ color: "var(--iio-text-3)" }}>{t("s4.selectNotSet")}</span>;
                return current.valid ? shown(entry, current.value) : t("s4.invalidOption", { v });
              }}
              sx={{
                width: "100%", height: 34, borderRadius: "6px", fontFamily: "inherit", fontSize: 14,
                color: "var(--iio-text-1)", background: "var(--iio-bg-0)",
                "& .MuiSelect-select": { py: 0, pl: "12px", pr: "36px !important", display: "flex", alignItems: "center", height: "34px !important", minHeight: 0 },
                "& .MuiSelect-icon": { color: "var(--iio-text-3)", right: 8 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-info)" },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-critical)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-info)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-info)", borderWidth: 1 },
                "&.Mui-focused": { boxShadow: "0 0 0 3px color-mix(in srgb, var(--iio-info) 22%, transparent)" },
              }}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: { className: "iio s4-select-menu", sx: {
                  mt: "4px", p: "4px", minWidth: 240, borderRadius: "8px",
                  backgroundColor: "var(--iio-bg-1)", backgroundImage: "none", color: "var(--iio-text-1)",
                  border: "1px solid var(--iio-border)", boxShadow: "var(--iio-shadow-md)",
                } },
                MenuListProps: { "aria-label": aria, sx: { p: 0 } },
              }}
            >
              {!current.valid && (
                <MenuItem value={editVal} disabled sx={{ fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, borderRadius: "6px", color: "var(--iio-critical)" }}>
                  {t("s4.invalidOption", { v: editVal })}
                </MenuItem>
              )}
              {[{ value: "", text: t("s4.selectNotSet") }, ...options.map(o => ({ value: o.value, text: shown(entry, o.value) }))].map(o => (
                <MenuItem key={o.value || "__unset__"} value={o.value} sx={{
                  fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, borderRadius: "6px", minHeight: 36,
                  color: o.value ? "var(--iio-text-1)" : "var(--iio-text-2)",
                  "&:hover, &.Mui-focusVisible": { backgroundColor: "var(--iio-bg-2)" },
                  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": { backgroundColor: "color-mix(in srgb, var(--iio-info) 10%, var(--iio-bg-1))" },
                }}>{o.text}</MenuItem>
              ))}
            </Select>
            {(!current.valid || mappedFrom) && (
              <FormHelperText error={!current.valid} sx={{ m: 0, fontFamily: "inherit", fontSize: 12, lineHeight: 1.4, color: current.valid ? "var(--iio-text-2)" : "var(--iio-critical)" }}>
                {current.valid
                  ? t("s4.valueMapped", { from: mappedFrom, to: current.value })
                  : t("s4.invalidValueHint", { field: adapterFieldName(entry.adapterField, lang) })}
              </FormHelperText>
            )}
          </>
        ) : (
          <input
            ref={inputRef}
            className="ce-txt"
            type="text"
            value={editVal}
            placeholder={placeholder}
            onChange={(e) => setEditVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); closeEditor(); }
              if (e.key === "Enter") { e.preventDefault(); commitEditor(); }
            }}
          />
        )}
        <div className="ce-foot">
          {!isBasis && ed.hasOverride && (
            <button type="button" className="ce-btn dngr" onClick={() => resetOverride(ed.artId, ed.paramKey, ed.stageKey)}>
              {t("s4.edRemove")}
            </button>
          )}
          <button type="button" className="ce-btn ghost" onClick={closeEditor}>{t("s4.edCancel")}</button>
          <button type="button" className="ce-btn pri" onClick={commitEditor}>{t("s4.edApply")}</button>
        </div>
      </div>
    );
  };

  return (
    <section className="scene active scene-enter" id="s4">
      {cfgFailed && <div className="errline" role="alert">
        <span>{t("batch.configError")}{gitCfgs.errorCode && <> {t(gitCfgs.errorCode === "TRANSPORT_READ_TIMEOUT" ? "s4.gitReadTimeout" : "s4.gitReadCode", { code: gitCfgs.errorCode })}</>}</span>
        <button className="btn btn-sec btn-sm" onClick={() => dispatch({ type: "RETRY_CONFIGS" })}>{t("err.retry")}</button>
      </div>}
      {/* A3: Hinweis nach Paketwechsel/Werkänderung, dass die Konfiguration zurückgesetzt wurde. */}
      {state.cfgInvalidated && !cfgLaedt && <div className="warnline" role="status">{t("s4.cfgReset")}</div>}
      <div className="sh">
        <h2>{t("s4.h2")}</h2>
        <p>{t("s4.p")}</p>
      </div>

      {!parametersNotRequired && <div className="cfg-head">
        <div className="progress-wrap">
          <div className="progress-label">
            <span>
              {cfgFailed ? t("batch.configUnavailable") : cfgLaedt ? t("s4.loading") : t("s4.progress", { x: activeCounts.filled, y: activeCounts.total })}
              {!cfgLaedt && withWerke && activeBucket ? t("s4.activeTab", { name: activeBucket.name }) : ""}
            </span>
            <span>{!cfgFailed && !cfgLaedt && activeCounts.total ? progressPct + "%" : "-"}</span>
          </div>
          <div className="progress-track">
            <div className={"progress-fill" + (!cfgFailed && progressPct >= 100 && activeCounts.total ? " full" : "")} style={{ width: (cfgFailed ? 0 : progressPct) + "%" }} />
          </div>
        </div>
        <button className="btn btn-sec" onClick={onFillAll} disabled={cfgLaedtIrgendwo}>{t(state.cfgSuggestionUndo ? "s4.undoFillAll" : "s4.fillAll")}</button>
      </div>}

      {targetInst && (
        <div className="inst-line">
          {!cfgFailed && !parametersNotRequired && <span className="st st--info">{t("s4.connected")}</span>}
          {targetInst.name}
          <span className="muted" style={{ fontWeight: 500 }}>
            · {t("s4.targetInst")}{withWerke ? ` · ${bucketList.length} ${t("rt.werke")}` : ""}
            {stufen.length > 1 ? ` · ${t("s4.stagesN", { n: stufen.length })}` : ""}
          </span>
        </div>
      )}

      <div className="utabs">
        {bucketList.map((b) => {
          const c = counts(b.key);
          const cls = c.filled === c.total ? "fill-ok" : "fill-miss";
          return (
            <button
              key={b.key}
              className={"utab" + (tab === b.key ? " on" : "")}
              onClick={() => dispatch({ type: "SET_CFG_TAB", payload: b.key })}
            >
              {b.name}
              {b.kennung && <> <span className="ken">{b.kennung}</span></>}
              {" · "}
              <span className={cfgFailed ? "muted" : cls}>{parametersNotRequired ? t("s4.parametersNotRequired") : cfgFailed ? t("batch.configUnavailable") : `${c.filled}/${c.total}`}</span>
            </button>
          );
        })}
      </div>

      <div>
        {designCount > 0 && <p className="tiny muted" role="note" style={{ margin: "0 0 12px" }}>{t("pkgT.config")}</p>}
        {arts.length === 0 && <div className="cred-box">{t(packageWithoutIflows ? "pkgT.configNoIflows" : "s4.noneWerk")}</div>}
        {!cfgFailed && arts.length > 0 && cfgLaedt && <Skeleton variant="cards" count={Math.min(arts.length, 4)} columns={1} label={t("s4.loading")} />}
        {!cfgFailed && !cfgLaedt && arts.map((a, idx) => {
          const entries = state.cfg?.[tab]?.[a.id]?.entries || [];
          const { sender, receiver, other } = bucketByGroup(entries);
          const isOpen = openAcc[a.id] ?? idx === 0;
          const missCnt = countMissing(entries);
          const pcfgEditing = editing && editing.artId === a.id;
          // Dynamische Spaltenbreiten: je mehr Stufen, desto enger ruecken Parameter-,
          // Quelle- und Status-Spalte zusammen, damit auch eine 4-System-Landschaft
          // (Parameter | Quelle | 4 Stufen | Status) ohne Horizontalscroll passt.
          // Die fr-Anteile fuellen die Restbreite gleichmaessig; Truncation (overflow
          // hidden an .field/.inherit/.p-key/.s-val/.stage-code) haelt jeden Text in seiner Spalte.
          const dense = stageCount >= 3;
          const paramMin = dense ? 150 : 190;
          const srcMin = dense ? 150 : 200;
          const stageMin = stageCount >= 4 ? 118 : stageCount === 3 ? 140 : 185;
          const statusMin = dense ? 116 : 140;
          const gridCols =
            `minmax(${paramMin}px, ${dense ? 0.9 : 1}fr) minmax(${srcMin}px, 1.15fr) ` +
            Array(stageCount).fill(`minmax(${stageMin}px, 1.1fr)`).join(" ") +
            ` minmax(${statusMin}px, 160px)`;
          const minW = paramMin + srcMin + stageCount * stageMin + statusMin + 8;
          return (
            <div key={a.id} className={"acc" + (isOpen ? " open" : "") + (pcfgEditing ? " editing-open" : "")}>
              <button className="acc-h" onClick={() => { closeEditor(); setOpenAcc((s) => ({ ...s, [a.id]: !isOpen })); }}>
                <span className={"exp-btn" + (isOpen ? " open" : "")}><svg className="ic ic-16"><use href="#ic-chevron-right" /></svg></span>
                <span className="acc-title">
                  {/* Werk: Servername mit hervorgehobener Kennung, darunter die Server-ID. Nie Quell-ID plus Kennung. */}
                  {activeBucket?.kennung && a.targetName
                    ? <TargetName source={a.name} target={a.targetName} kennung={activeBucket.kennung} /> : a.name}
                  <span className="art-id">
                    {activeBucket?.kennung && a.ownPlant ? a.suffixedId : a.id}
                    {" · "}{t("st." + (a.status || "identical"))}
                  </span>
                </span>
                {(() => { const h = missingHint(t, missCnt); return <span className={"st st--" + h.cls}>{h.text}</span>; })()}
              </button>
              {isOpen && (
                <div className="acc-body">
                  <div className={"pcfg-scroll" + (pcfgEditing ? " editing" : "")}>
                    <div className={"pcfg" + (pcfgEditing ? " editing" : "") + (multi ? " multi" : "")} style={{ gridTemplateColumns: gridCols, minWidth: minW }}>
                      {/* Kopfzeile */}
                      <div className="pcfg-row pcfg-head" role="row">
                        <div className="pcfg-cell hd">{t("th.param")}</div>
                        <div className="pcfg-cell hd">{quellCode ? `${t("th.src")} (${quellCode})` : t("th.src")}</div>
                        {effektiveStufen.map((st, sidx) => (
                          <div key={st.key} className="pcfg-cell hd stage">
                            <span className="stage-code" title={multi ? st.name : undefined}>{st.code || t("th.tgt")}</span>
                            {multi && <span className="stage-sub" title={st.name}>{st.region || st.name}</span>}
                            {stageCount > 1 && (
                              <span className="stage-sub">{sidx === 0 ? t("s4.hdrBasis") : t("s4.hdrOverlay")}</span>
                            )}
                          </div>
                        ))}
                        <div className="pcfg-cell hd">{t("th.status")}</div>
                      </div>

                      {[{ g: "s4.grpSender", list: sender }, { g: "s4.grpReceiver", list: receiver }, { g: "s4.grpGeneral", list: other }].map(({ g, list }, gi) => (
                        list.length > 0 && (
                          <React.Fragment key={g + gi}>
                            <div className="pcfg-grp" role="row"><span>{t(g)}</span> <span className="grp-count">{list.length}</span></div>
                            {list.map((e) => {
                              const isEditingRow = pcfgEditing && e.ParameterKey === editing.paramKey;
                              const rowEditRowCls = isEditingRow ? " editing-row" : "";
                              // Abgeblendete Zeilen (Spotlight) auch fuer Screenreader ausblenden.
                              const dimHidden = pcfgEditing && !isEditingRow ? true : undefined;
                              const baseStatus = classifyConfigStatus(e);
                              const baseReq = baseStatus === "required";
                              const baseInvalid = baseStatus === "invalid";
                              const baseVal = e.newValue || "";
                              const rowSt = computeRowStatus(e, isEditingRow);
                              const baseHasEff = !!effektiverBasisWert(e);
                              return (
                                <div className="pcfg-row" role="row" key={e.ParameterKey}>
                                  <div className={"pcfg-cell p" + rowEditRowCls} aria-hidden={dimHidden}>
                                    <span className="p-key" title={e.ParameterKey}>{e.ParameterKey}</span>
                                    <span className="p-meta">
                                      {baseReq && <span className="req">{t("s4.required")}</span>}
                                      {baseReq ? " · " : ""}{e.DataType || "xsd:string"}
                                    </span>
                                  </div>
                                  <div className={"pcfg-cell s" + rowEditRowCls} title={shown(e, e.defaultValue) || ""} aria-hidden={dimHidden}>
                                    <span className={"s-val" + (e.defaultValue ? "" : " empty")}>{shown(e, e.defaultValue) || "-"}</span>
                                  </div>

                                  {effektiveStufen.map((st, sidx) => {
                                    const isBasis = sidx === 0;
                                    const isEditingCell = isEditingRow && editing.stageKey === st.key;
                                    const hideCls = isEditingCell ? { visibility: "hidden" } : undefined;
                                    let cellInner, tipText = null, cellHint = null;
                                    if (isBasis) {
                                      if (multi) tipText = originTitle(e, st, sidx, null, shown(e, baseVal) || shown(e, e.defaultValue) || "-");
                                      // Auswahlfeld: Hinweis, dass eine Bezeichnung umgesetzt wurde, bzw. dass der Wert ungültig ist.
                                      if (baseInvalid) cellHint = { error: true, text: t("s4.invalidValueHint", { field: adapterFieldName(e.adapterField, lang) }) };
                                      else if (e.mappedFrom && !e.modified) cellHint = { error: false, text: t("s4.valueMapped", { from: e.mappedFrom, to: baseVal }) };
                                      cellInner = (
                                        <div
                                          className={"field" + ((baseReq && !baseVal) || baseInvalid ? " invalid" : "") + (targetMissing(e, st, sidx) && !(baseReq && !baseVal) ? " target-missing" : "")}
                                          style={hideCls}
                                          role="button"
                                          tabIndex={isEditingCell ? -1 : 0}
                                          aria-label={t("s4.edAriaBasis", { param: e.ParameterKey }) + (targetMissing(e, st, sidx) ? ". " + t("s4.targetMissing") : "")}
                                          title={multi ? undefined : (shown(e, baseVal) || shown(e, e.defaultValue) || "-")}
                                          onClick={() => openEditor(a.id, e, st, sidx)}
                                          onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openEditor(a.id, e, st, sidx); } }}
                                        >
                                          <span className={"field-txt" + (baseVal ? "" : " placeholder")}>
                                            {shown(e, baseVal) || (e.modified ? t("s4.emptyLabel") : (shown(e, e.defaultValue) || "-"))}
                                          </span>
                                          {EDIT_GLYPH}
                                        </div>
                                      );
                                    } else {
                                      const inh = geerbterWert(e, effektiveStufen, sidx, chainCtx);
                                      const ov = e?.byStage?.[st.key]?.newValue || "";
                                      const isOverride = !!ov && !sameParameterValue(ov, inh.value);
                                      // D2 Hinweis: Zieltenant-Wert anzeigen, wenn er vom geerbten/eingetragenen Wert abweicht.
                                      const stageTarget = e?.byStage?.[st.key]?.targetValue || "";
                                      const shownValue = isOverride ? ov : inh.value;
                                      const targetDiffers = !!stageTarget && !sameParameterValue(stageTarget, shownValue);
                                      if (multi) tipText = originTitle(e, st, sidx, inh, isOverride ? `${t("s4.divergeTitle")}: ${shown(e, ov)}` : t("s4.inherits", { v: shown(e, inh.value) || "-" }));
                                      const stageCell = e?.byStage?.[st.key];
                                      if (isOverride && !normalizeAdapterFieldValue(e.adapterField, ov).valid) cellHint = { error: true, text: t("s4.invalidValueHint", { field: adapterFieldName(e.adapterField, lang) }) };
                                      else if (isOverride && stageCell?.mappedFrom && !stageCell.modified) cellHint = { error: false, text: t("s4.valueMapped", { from: stageCell.mappedFrom, to: ov }) };
                                      if (!baseHasEff && !isOverride) {
                                        if (multi) tipText = [tipText, t("s4.setBaseFirst")].filter(Boolean).join("\n");
                                        cellInner = stageTarget ? (
                                          <div className="inherit" style={hideCls} onClick={() => openEditor(a.id, e, st, sidx)}>
                                            <span className="inherit-txt">{t("s4.inheritEmpty")}</span>
                                            <span className="stage-target-hint">
                                              <span className="hint-txt" title={stageTarget}>{t("s4.stageTargetHint", { v: shown(e, stageTarget) })}</span>
                                              <button type="button" className="adopt-btn" onClick={(ev) => { ev.stopPropagation(); onPatchStage(tab, a.id, e.ParameterKey, st.key, stageTarget); }}>{t("s4.adoptTarget")}</button>
                                            </span>
                                          </div>
                                        ) : (
                                          <div className="inherit disabled" title={multi ? undefined : t("s4.setBaseFirst")}>
                                            <span className="inherit-txt">{t("s4.inheritEmpty")}</span>
                                          </div>
                                        );
                                      } else if (isOverride) {
                                        cellInner = (
                                          <div
                                            className={"field override" + (cellHint?.error ? " invalid" : "") + (targetMissing(e, st, sidx) ? " target-missing" : "")}
                                            style={hideCls}
                                            role="button"
                                            tabIndex={isEditingCell ? -1 : 0}
                                            aria-label={t("s4.edAriaOverride", { param: e.ParameterKey, stage: st.code }) + (targetMissing(e, st, sidx) ? ". " + t("s4.targetMissing") : "")}
                                            title={multi ? undefined : `${t("s4.divergeTitle")}: ${shown(e, ov)}`}
                                            onClick={() => openEditor(a.id, e, st, sidx)}
                                            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openEditor(a.id, e, st, sidx); } }}
                                          >
                                            <span className="field-txt">{shown(e, ov)}</span>
                                            <button
                                              type="button"
                                              className="reset-btn"
                                              title={t("s4.edResetTitle")}
                                              aria-label={t("s4.edResetTitle")}
                                              onClick={(ev) => { ev.stopPropagation(); resetOverride(a.id, e.ParameterKey, st.key); }}
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
                                            </button>
                                            {targetDiffers && (
                                              <span className="stage-target-hint">
                                                <span className="hint-txt" title={stageTarget}>{t("s4.stageTargetHint", { v: shown(e, stageTarget) })}</span>
                                              </span>
                                            )}
                                          </div>
                                        );
                                      } else {
                                        cellInner = (
                                          <div className={"inherit" + (targetMissing(e, st, sidx) ? " target-missing" : "")} style={hideCls} onClick={() => openEditor(a.id, e, st, sidx)}
                                            {...(multi ? { role: "group", "aria-label": t("s4.inherits", { v: shown(e, inh.value) || "-" }) + (targetMissing(e, st, sidx) ? ". " + t("s4.targetMissing") : "") } : {})}>
                                            <span className="inherit-txt" title={multi ? undefined : t("s4.inherits", { v: shown(e, inh.value) || "-" })}>
                                              {t("s4.inherits", { v: shown(e, inh.value) || "-" })}
                                            </span>
                                            <button
                                              type="button"
                                              className="add-btn"
                                              aria-label={t("s4.edAriaOverride", { param: e.ParameterKey, stage: st.code })}
                                              onClick={(ev) => { ev.stopPropagation(); openEditor(a.id, e, st, sidx); }}
                                            >
                                              <span className="plus">+</span> {t("s4.addOverride")}
                                            </button>
                                            {targetDiffers && (
                                              <span className="stage-target-hint">
                                                <span className="hint-txt" title={stageTarget}>{t("s4.stageTargetHint", { v: shown(e, stageTarget) })}</span>
                                                <button type="button" className="adopt-btn" onClick={(ev) => { ev.stopPropagation(); onPatchStage(tab, a.id, e.ParameterKey, st.key, stageTarget); }}>{t("s4.adoptTarget")}</button>
                                              </span>
                                            )}
                                          </div>
                                        );
                                      }
                                    }
                                    return (
                                      <div key={st.key} className={"pcfg-cell stage" + rowEditRowCls + (isEditingCell ? " editing-cell" : "")} aria-hidden={dimHidden}>
                                        {cellHint ? (
                                          <div className="cell-stack" style={hideCls}>
                                            {tipText ? tip(tipText, cellInner) : cellInner}
                                            <FormHelperText component="span" error={cellHint.error} className="cell-hint" sx={{ m: 0, fontFamily: "inherit", fontSize: 11.5, lineHeight: 1.35, color: cellHint.error ? "var(--iio-critical)" : "var(--iio-text-2)" }}>
                                              {cellHint.text}
                                            </FormHelperText>
                                          </div>
                                        ) : (tipText ? tip(tipText, cellInner) : cellInner)}
                                        {isEditingCell && renderEditor(e, st, sidx)}
                                      </div>
                                    );
                                  })}

                                  <div className={"pcfg-cell stat" + rowEditRowCls} aria-hidden={dimHidden}>
                                    <span className={"st st--" + rowSt.cls}>{rowSt.label}</span>
                                    {missingCount(e) > 0 && <span className="st st--warn target-missing-text">{missingCount(e) > 1 ? t("s4.targetMissingN", { n: missingCount(e) }) : t("s4.targetMissing")}</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        )
                      ))}
                    </div>
                  </div>
                  {stufen.length > 1 && (
                    <div className="pcfg-hint">{t("s4.stageInheritHint")}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="cred-box" dangerouslySetInnerHTML={{ __html: t("s4.cred") }} />

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 3 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span className="foot-hint">
            {parametersNotRequired ? t("s4.parametersNotRequired") : cfgFailed ? t("batch.configUnavailable") : cfgLaedtIrgendwo ? t("s4.loading") : missingHint(t, activeCounts).text}
          </span>
          <button className="btn btn-cta" onClick={onWeiter} disabled={cfgLaedtIrgendwo}>
            {t("s4.next")}
            <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
          </button>
        </div>
      </div>

      {/* Warn-Popup nativ als .modal-ov (kein MUI-Portal). */}
      <div
        className={"modal-ov" + (showWarn ? " show" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s4-warn-title"
        onClick={(e) => { if (e.target === e.currentTarget) setShowWarn(false); }}
      >
        <div className="modal">
          <div className="modal-h">
            <h3 id="s4-warn-title">{t("s4.warnTitle")}</h3>
            <button type="button" className="modal-x" aria-label={t("common.close")} onClick={() => setShowWarn(false)}>
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>
          </div>
          <div className="modal-b">
            <p style={{ margin: 0, color: "var(--t2)" }}>
              {offenReq === 1 ? t("s4.warnBodyOne") : t("s4.warnBody", { n: offenReq })}
            </p>
          </div>
          <div className="modal-h" style={{ borderTop: "1px solid var(--border-sub)", borderBottom: "none", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn btn-sec" onClick={() => setShowWarn(false)}>
              {t("s4.warnCancel")}
            </button>
            <button type="button" className="btn btn-cta" onClick={bestaetigeWeiter}>
              {t("s4.warnGo")}
              <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
